import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { InputField, InputType, ProviderName } from '../../../account/models/login-response.model';
import { config } from '../../../shared/config';
import { ApiService } from '../../../shared/services/core/api.service';
import { AuthService } from '../../../user/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class OtherMediaAuthService {
  /**
   * This holds all the other media settings required for sso,
   * this information will be read from the config js file
   * and also passed or defaulted from the login process.
   */
  
  constructor(
    private authService: AuthService,
    private api: ApiService,
    private cookieService: CookieService
  ) {
  }

  /**
   * Attempt to login using key cloake using redirect process.
   * This will call an endpoint to generate the login URL,
   * depending on the parameters passed.
   * This can also be used for registration, handled by the parameters passed.
   * @param successredirectPath redirect path after successfully authenticated
   * e.g. /sa-callback (which is the default value)
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   */
  login(successredirectPath: string = '/om-callback', clubId: number = 1) {
    const loginUrlParameters: InputField[] = this.generateLoginParameters(successredirectPath);

    return this.api.post(`sso/OAuth/GenerateUrl/${ProviderName.OtherMedia}/${clubId}`, loginUrlParameters).pipe(
      tap((generatedUrl: any) => {
        window.location.href = generatedUrl;
      })
    );
  }

  /**
   * This method will generate the login
   * URL for other media from the defined
   * settings, when login has been attempted.
   *
   * @returns array of parameters required
   * to generate the Other Media
   * login URL of type InputField.
   */
  generateLoginParameters(successredirectPath: string) {
    let generateLoginParams: InputField[] = [];

    let returnVisitParam: InputField = {
      FieldName: 'RedirectUrl',
      InputType: InputType.Text,
      Active: true,
      Value: config.portalUrl + successredirectPath,
      ErrorMessage: '',
    };

    generateLoginParams.push(returnVisitParam);

    return generateLoginParams;
  }

  /**
   * This method is used to authenticate a token
   * that comes back from the Other Media login page
   * and log the user in via the tokens returned
   * from the response.
   * @param code code returned from
   * Other Media, to be authenticated and
   * passed to given endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  validateCode(code: string, clubId: number = 1) {
    if (this.authService.hasAccessToken()) {
      return of(true);
    }

    return this.api.post(`sso/OAuth/${ProviderName.OtherMedia}/${code}/${clubId}`, `"${code}"`).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          localStorage.setItem('OMRefreshToken', authResponse.auth.RefreshToken.toString());
          localStorage.setItem('OMAccessToken', authResponse.auth.AccessToken.toString());
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
        }
      }),
      map((authResponse: any) => {
        if (authResponse) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Log out of the portal and provider (Other Media).
   * Passes provider tokens and provider ID
   * to logout for the logout endpoint
   * with portal provider details.
   */
  logout() {
    let providerTokens: { [name: string]: string | null } = {};
    providerTokens.providerRefreshToken = localStorage.getItem('OMRefreshToken');
    providerTokens.providerAccessToken = localStorage.getItem('OMccessToken');
    localStorage.removeItem('OMRefreshToken');
    localStorage.removeItem('OMAccessToken');

    return this.authService.logout(ProviderName.Fortress, providerTokens);
  }

  /**
   * This method is used to authenticate a domain
   * token used from the _sa_sso_upid cookie given
   * from Other Media domain and log the user
   * using tokens returned.
   * @param domainCookie domain cookie to be
   * authenticated and passed to endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  loginUsingToken(domainCookie: string, clubId: number = 1) {
    let saDomainCookie = '';
    if (domainCookie != '' && domainCookie != undefined) {
      saDomainCookie = domainCookie;
    }

    return this.api.get(`sso/OAuth/${ProviderName.OtherMedia}/${saDomainCookie}/${clubId}`).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
          localStorage.setItem('SARefreshToken', authResponse.auth.RefreshToken.toString());
          localStorage.setItem('SAAccessToken', authResponse.auth.AccessToken.toString());
        }
      }),
      map((authResponse: any) => {
        if ((authResponse.status = 200)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Check if a named cookie exists.
   * @param cookieName cookie name to be checked.
   * @returns boolean value depending if cookie name
   * exists.
   */
  hasNamedCookie(cookieName: string): boolean {
    if (this.cookieService.check(cookieName)) {
      return true;
    }
    return false;
  }
}
